import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatTabGroup } from '@angular/material/tabs';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TrainingService } from '../../../services/training/training.service';
import { TrainingModuleDetail } from '../../../services/training/training.interface';
import { TrainingContentComponent } from './training-content/training-content.component';
import { TrainingQuizComponent } from './training-quiz/training-quiz.component';

@Component({
  selector: 'app-training-module',
  standalone: true,
  imports: [
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TrainingContentComponent,
    TrainingQuizComponent,
  ],
  templateUrl: './training-module.component.html',
  styleUrl: './training-module.component.css',
})
export class TrainingModuleComponent implements OnInit {
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;

  detail: TrainingModuleDetail | null = null;
  loading = true;
  contentCompleted = false;
  justCompletedContent = false;
  hasQuiz = false;
  quizInProgress = false;
  sectionIndex = 0;
  markingComplete = false;
  completedSectionIndices = new Set<number>();

  constructor(
    private route: ActivatedRoute,
    private trainingService: TrainingService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id')!;
    this.trainingService.getModule(id).subscribe({
      next: (detail) => {
        this.detail = detail;
        this.contentCompleted = !!detail.progress?.contentCompletedAt;
        this.loading = false;
      },
      error: () => { this.loading = false; },
    });
    this.trainingService.getQuiz(id).subscribe({
      next: (quiz) => { this.hasQuiz = quiz.questions.length > 0; },
      error: () => { this.hasQuiz = false; },
    });
  }

  get currentSection() {
    return this.detail?.sections[this.sectionIndex] ?? null;
  }

  get isLastSection(): boolean {
    return this.sectionIndex === (this.detail?.sections.length ?? 1) - 1;
  }

  get currentSectionCompleted(): boolean {
    return this.completedSectionIndices.has(this.sectionIndex) || this.contentCompleted;
  }

  isSectionDone(index: number): boolean {
    return this.completedSectionIndices.has(index) || this.contentCompleted;
  }

  onSectionDone(): void {
    this.completedSectionIndices.add(this.sectionIndex);
    if (this.isLastSection) {
      this.markingComplete = true;
      this.trainingService.markContentComplete(this.detail!.module._id).subscribe({
        next: () => {
          this.markingComplete = false;
          this.contentCompleted = true;
          this.justCompletedContent = true;
        },
        error: () => { this.markingComplete = false; },
      });
    }
  }

  nextSection(): void {
    if (!this.isLastSection) this.sectionIndex++;
  }

  prevSection(): void {
    if (this.sectionIndex > 0) this.sectionIndex--;
  }

  goToQuiz(): void {
    this.quizInProgress = true;
    this.tabGroup.selectedIndex = 1;
  }

  onQuizSubmitted(): void {
    this.quizInProgress = false;
  }

  onTabIndexChange(index: number): void {
    if (index === 1) {
      this.quizInProgress = true;
    } else if (this.quizInProgress) {
      setTimeout(() => this.tabGroup.selectedIndex = 1);
    }
  }

  goBack(): void {
    window.close();
  }
}
